/**
 * partners.js — Partner management/scoring domain service.
 */
import {
  toApiUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
  handleUnauthorizedResponse,
  normalizeApiList,
  requestJson,
  extractAnyId,
} from './_core';

export async function getPartnerScore(token = null, query = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const candidatePaths = ['/partners/dashboard/', '/partners/score/'];
  let lastErrorMessage = '';
  let lastErrorStatus = 0;

  for (let i = 0; i < candidatePaths.length; i++) {
    const path = candidatePaths[i];
    const isLast = i === candidatePaths.length - 1;
    const response = await fetchWithAuthRetry(toApiUrl(`${path}${qs}`), jsonFetchOptions(token), token);
    if (response.ok) {
      return parseJsonOrThrow(response, 'Partner score');
    }

    // Fall back only when the endpoint is missing (older backends). A 403 is the
    // real answer: retrying /partners/score/ just replaced it with a misleading 400.
    if (response.status === 404 && !isLast) {
      continue;
    }

    if (response.status === 404 && isLast) {
      break;
    }

    const msg = await readApiErrorMessage(response, `Partner score failed (${response.status})`);
    lastErrorMessage = msg;
    lastErrorStatus = response.status;
    if (response.status === 401) {
      await handleUnauthorizedResponse(response);
    }
    break;
  }

  if (lastErrorMessage) {
    const err = new Error(lastErrorMessage);
    err.status = lastErrorStatus;
    throw err;
  }

  const missing = new Error('Partner score is not available right now. Please try again later.');
  missing.status = 404;
  throw missing;
}

export const getPartnerClients = (token = null, query = null) =>
  requestJson('GET', '/partners/clients/', null, token, query || undefined).then((data) => normalizeApiList(data));

export const listPartners = (token = null) =>
  requestJson('GET', '/partners/manage/', null, token).then((data) => normalizeApiList(data));

export const createPartner = (payload, token = null) =>
  requestJson('POST', '/partners/manage/', payload, token);

export const updatePartner = (payload, token = null) => {
  const partnerId = extractAnyId(payload, ['partner_id', 'id', 'uu_id']);
  if (!partnerId) throw new Error('partner_id is required');
  const body = { ...payload };
  delete body.partner_id;
  delete body.id;
  delete body.uu_id;
  return requestJson('PATCH', '/partners/manage/', body, token, { partner_id: partnerId });
};

export const deletePartner = (payload, token = null) => {
  const partnerId = extractAnyId(payload, ['partner_id', 'id', 'uu_id']);
  if (!partnerId) throw new Error('partner_id is required');
  return requestJson('DELETE', '/partners/manage/', null, token, { partner_id: partnerId });
};
